/**
 * Filters the Vulnerabilities list offers that `GET /vulnerabilities/filter/`
 * does not advertise: CISA KEV and Risk.
 *
 * They go through the same `Filter` component as every backend-driven category.
 * The merge is one-way — anything the backend returns wins, and these entries
 * only fill a key the response lacks.
 *
 * - `kev` is a single-option category: on sends `kev=true`, off sends nothing.
 * - `risk` is ONE category (Critical / High / Medium / Low). It is UI-only:
 *   `riskRequestParams` rewrites it into the endpoint's own `risk_min` /
 *   `risk_max` before the request, so `risk` itself is never sent.
 */
import type { OptionItemType } from '../../../modules/types/filters'

export const KEV_FILTER = 'kev'
export const RISK_FILTER = 'risk'

export const RISK_BANDS = ['critical', 'high', 'medium', 'low'] as const

/**
 * Inclusive score range per band. Mirrors the guide's band cut-offs (critical
 * >= 85, high >= 65, medium >= 40); the backend may tune them, and these only
 * decide which bounds a chosen band sends.
 */
const RISK_BAND_RANGES: Record<string, [number, number]> = {
  critical: [85, 100],
  high: [65, 84],
  medium: [40, 64],
  low: [0, 39],
}

/**
 * The local stand-in options. `label` is already translated by the caller; the
 * band words reuse the severity message ids (`critical` … `low`).
 */
export function localFilterOptions(labels: {
  kev: string
  bands: Record<string, string>
}): Record<string, OptionItemType[]> {
  return {
    [KEV_FILTER]: [{ name: 'true', label: labels.kev }],
    [RISK_FILTER]: RISK_BANDS.map((band) => ({ name: band, label: labels.bands[band] })),
  }
}

/**
 * Rewrite the UI-only `risk` selection into `risk_min` / `risk_max` (the span
 * from the lowest chosen band's floor to the highest chosen band's ceiling).
 * Non-adjacent bands therefore widen to the span between them, since the
 * endpoint takes a single range. Every other param passes through untouched.
 */
export function riskRequestParams(query: string): string {
  const params = new URLSearchParams(query)
  const selected = params.get(RISK_FILTER)
  if (selected === null) return query
  params.delete(RISK_FILTER)
  const ranges = selected
    .split(',')
    .map((band) => RISK_BAND_RANGES[band])
    .filter(Boolean)
  if (ranges.length > 0) {
    params.set('risk_min', String(Math.min(...ranges.map(([min]) => min))))
    params.set('risk_max', String(Math.max(...ranges.map(([, max]) => max))))
  }
  return params.toString()
}
